<!-- eslint-disable vue/no-restricted-html-elements -->

<template>
  <div
    v-if="variantColourTitle"
    class="product-colour-selector"
    :class="[
      { 'is-static': !variantColourTotal || variantColourTotal < 1 }
    ]"
    @click.prevent="handleColourSelectorClick"
  >
    <div
      v-if="product.swatchImage"
      class="product-colour-selector__image"
    >
      <img
        class="product-colour-selector__swatch"
        :src="product.swatchImage"
        :alt="variantColourTitle || product?.title"
        sizes="(min-width: 1024px) 82px, 59px"
      >
    </div>

    <div class="product-colour-selector__details">
      <p
        class="product-colour-selector__title text-body-s-bold text-body-l-bold-desktop"
        v-text="variantColourTitle"
      />

      <div class="product-colour-selector__colour-swatch-container">
        <colour-swatch
          :liquid="liquid"
          :product="localProduct"
          :colours="localColours"
          location="colour-swatch--product-colour-selector"
        />

        <p
          v-if="variantColourTotal && variantColourTotal > 0"
          class="product-colour-selector__subtitle text-body-xs-regular text-body-s-regular-desktop"
          v-text="$string(
            'product.colours_available',
            {
              replace: { num: variantColourTotal},
              pluralise: variantColourTotal,
            }
          )"
        />
      </div>
    </div>

    <div
      v-if="variantColourTotal && variantColourTotal > 0"
      class="product-colour-selector__icon"
    >
      <icon-chevron-right />
    </div>
  </div>

  <teleport to="body">
    <overlay
      key="colourSelectorDrawer"
      block-class="colour-selector-drawer-overlay"
      namespace="colourSelectorDrawer"
      type="drawer"
      direction="up-left"
    >
      <template #title>
        <p
          class="colour-selector__title text-body-l-bold"
          v-text="$string('product.form.select_a_colour')"
        />
      </template>

      <template #body="overlay">
        <colour-selector-drawer
          :overlay="overlay"
          :product="product"
          :variant="variant"
          :swatches="relatedProductSwatches"
          :back-in-stock="backInStock"
        />
      </template>
    </overlay>
  </teleport>
</template>

<script>

/**
 * Vue: Product colour selector (product-colour-selector)
 * -----------------------------------------------------------------------------
 * Component containing product colour selector display and drawer.
 *
 * @param {Object|Boolean} product - Canvas formatted product object.
 * @param {Array} relatedProducts - Array containing canvas formatted color
 * related products.
 * @param {Object|Boolean} variant - Canvas formatted variant object.
 *
 */
import { mapActions } from 'vuex'

import Overlay from '~global/overlay/overlay'

import ColourSelectorDrawer from '~async/colour-selector-drawer/colour-selector-drawer'
import IconChevronRight from '~icons/directional-navigation/chevron/right-offset.svg'
import ColourSwatch from '~async/product-card/colour-swatch/colour-swatch'

export default {
  name: 'ProductColourSelector',

  components: {
    ColourSelectorDrawer,
    IconChevronRight,
    Overlay,
    ColourSwatch,
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        enableColourSwatch: true,
      }),
      required: true,
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    relatedProducts: {
      type: Array,
      default: () => [],
    },
    variant: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    backInStock: {
      type: Object,
      default: () => ({
        enabled: true,
      }),
    },
  },

  data() {
    return {
      localProduct: {},
      localColours: [],
    }
  },

  computed: {

    /**
     * Compute color variant title.
     * @returns {String}
     */
    variantColourTitle() {
      const variant = this.variant || this.product?.selectedOrFirstAvailableVariant

      const colorVariant = variant?.selectedOptions?.find((option) => {
        const optionName = option.name?.toLowerCase()
        return optionName === 'colour' || optionName === 'color'
      })

      return colorVariant ? colorVariant.value : ''
    },

    /**
     * Compute total number of related colors.
     * @returns {Number}
     */
    variantColourTotal() {
      if (!this.relatedProducts) {
        return 0
      }

      return this.relatedProducts.length - 1
    },

    /**
     * Compute swatch objects for colour selector drawer.
     * @returns {Array}
     */
    relatedProductSwatches() {
      const swatches = this.relatedProducts.map((product) => {
        const colorVariant =
          product.selectedOrFirstAvailableVariant?.selectedOptions.find(
            (option) => {
              const optionName = option.name.toLowerCase()
              return optionName === 'colour' || optionName === 'color'
            },
          )

        return {
          id: product.id,
          available: product.available,
          image: product.swatchImage,
          url: product.url,
          title: colorVariant?.value || '',
          tags: product.tags,
          variants: product.variants,
        }
      })

      return swatches
    },
  },

  mounted() {
    this.localColours = this.relatedProducts
    this.localProduct = this.product

    this.localColours = this.localColours?.sort((item) => {
      return item?.handle === this.localProduct?.handle ? -1 : 0
    })
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      openOverlay: 'overlays/open',
    }),

    /**
     * Handle click event for colour selector
     */
    handleColourSelectorClick() {
      if (!this.variantColourTotal || this.variantColourTotal < 1) {
        return
      }

      this.openOverlay({
        component: 'colour-selector-drawer',
        ignoreDismissed: true,
        namespace: 'colourSelectorDrawer',
      })
    },
  },
}
</script>

<style lang="scss">
@import "./product-colour-selector";
</style>
